import { createSelector } from 'reselect';
import { ArmyListUnitExportView, Export } from './view/export';
import {
  getArmy,
  getArmyList,
  getArmyListUnits,
  getArmyOrganisation,
} from './exportSelector';
import { buildArmyOrganisationView } from '../make/army-organisation.presenter';
import { AppState } from '@Redux/store';
import { IArmyListExportOldWorldProps } from '@Builder/myArmyList/export/mode/ArmyListExportOldWorld';
import { Army, ArmyList, ConstraintItemType, VersionGame } from '@Models';
import { selectedElementsPresenter } from '@Selectors/builder/myArmyList/make/validation/selected-elements.presenter';

const isMarkdown = (_: AppState, owsProps: IArmyListExportOldWorldProps) =>
  owsProps.markdown;

const exportToClipboard = (
  armyList: ArmyList,
  name: string,
  army: Army,
  game: string,
  units: ArmyListUnitExportView[],
): string => {
  return [
    '===',
    `${armyList.creator ? `“${armyList.creator}“ - ` : ''}${name} [${armyList.points} pts]`,
    `${game}, ${army.nameWithSubVersion}`,
    '===',
    ...units.flatMap((unit) => [
      '',
      `++ ${unit.name} ++`,
      ...unit.armyListUnits.flatMap((armyListUnit) => [
        '',
        `${armyListUnit.size > 1 ? `${armyListUnit.size} ` : ''}${armyListUnit.unitName} [${armyListUnit.points} pts]`,
        ...armyListUnit.army_list_unit_unit_options.map((option) => {
          return `- ${option.name}${option.quantity > 1 ? ` x${option.quantity}` : ''}`;
        }),
      ]),
    ]),
    '\n' + 'Créé avec "9Th Builder"\n' + '[https://www.9thbuilder.com]\n',
  ].join('\n');
};

const exportMarkdownToClipboard = (
  armyList: ArmyList,
  name: string,
  army: Army,
  game: string,
  units: ArmyListUnitExportView[],
): string => {
  return [
    `## ${armyList.creator ? `“${armyList.creator}“ - ` : ''}${name} [${armyList.points} pts]`,
    `${game}, ${army.nameWithSubVersion}`,
    ...units.flatMap((unit) => [
      '',
      `### ${unit.name}`,
      '',
      ...unit.armyListUnits.flatMap((armyListUnit, index, self) => {
        const unitTxt = [
          `- ${armyListUnit.size > 1 ? `${armyListUnit.size} ` : ''}${armyListUnit.unitName} [${armyListUnit.points} pts]`,
        ];

        if (armyListUnit.army_list_unit_unit_options.length > 0) {
          unitTxt.push(
            ` -# (${armyListUnit.army_list_unit_unit_options
              .map((option) => {
                return `${option.name}${option.quantity > 1 ? ` x${option.quantity}` : ''}`;
              })
              .join(', ')})`,
          );
        }

        if (index !== self.length - 1) {
          unitTxt.push('');
        }

        return unitTxt;
      }),
    ]),
    '\n' + '*Créé avec "9Th Builder"* https://www.9thbuilder.com',
  ].join('\n');
};

export const displayExportTxtOldWorld = createSelector(
  [
    getArmy,
    getArmyList,
    getArmyOrganisation,
    getArmyListUnits,
    isMarkdown,
    selectedElementsPresenter,
  ],
  (
    army,
    armyList,
    armyOrganisation,
    armyListUnits,
    markdom,
    selectedElements,
  ): Export<ArmyListUnitExportView> => {
    if (!army || !armyList) {
      return {
        name: '',
        army: '',
        game: '',
        points: 0,
        armyListUnits: [],
        exportTxt: '',
      } satisfies Export<ArmyListUnitExportView> as Export<ArmyListUnitExportView>;
    }

    let name = armyList.name.trim();
    if (name.indexOf('(') != -1) {
      name = name.substring(0, name.indexOf('(')).trim();
    }

    const units = new Array<ArmyListUnitExportView>();
    army.organisations?.forEach((organisation) => {
      const army_list_units = armyListUnits
        .filter((item) => item.organisation_id == organisation.id)
        .sort((a, b) => a.position - b.position);

      const organisation_group =
        armyOrganisation!.army_organisation_groups.find(
          (item) => item.organisationId == organisation.id,
        );

      if (army_list_units.length != 0) {
        const seletedElement = selectedElements.find(
          (item) =>
            item.constrained.id === organisation.id &&
            item.constrained.type === ConstraintItemType.Organisation,
        );

        const armyOrganisationViews = buildArmyOrganisationView(
          seletedElement!.constrained,
          organisation_group!,
          seletedElement!,
          armyList?.max ?? 2000,
        );

        const organisationName = `${organisation.name} [${armyOrganisationViews
          .map((item) => item.current)
          .join('')}]`;

        units.push({
          name: organisationName,
          armyListUnits: army_list_units,
        } satisfies ArmyListUnitExportView as ArmyListUnitExportView);
      }
    });

    let game = '';
    switch (armyList.versionGame) {
      case VersionGame.TheOldWorld:
        game = 'Warhammer: The Old World';
        break;
      case VersionGame.EssenceOfWar:
        game = 'Essence of War';
        break;
      case VersionGame.WarhammerBattle:
        game = 'Warhammer Battle';
        break;
      case VersionGame.NinthAge:
        game = 'The Ninth Age';
        break;
    }

    return {
      name: `${armyList.creator ? `“${armyList.creator}“ - ` : ''}${name}`,
      army: army.nameWithSubVersion,
      game: game,
      points: armyList.points,
      armyListUnits: units,
      exportTxt: markdom
        ? exportMarkdownToClipboard(armyList, name, army, game, units)
        : exportToClipboard(armyList, name, army, game, units),
    } satisfies Export<ArmyListUnitExportView> as Export<ArmyListUnitExportView>;
  },
);
